// The scanner's shared parts: the bar above a screen, the scan field every screen
// hands focus back to, the segmented keys, a quantity with thumb-size steppers and the
// in-place answer line. Sized for one hand on a 360-424px screen: every key is 44px or
// taller, and fields are filled, never outlined.

import React, { useState } from 'react';
import { fieldBase } from '../components/review/fieldStyles';
import { FOCUS, MOTION, STATE_LAYER } from '../Requisitions/buttonStyles';
import { BackIcon, CheckIcon, MinusIcon, PlusIcon, ScanFrameIcon, WarningIcon } from './icons';
import { inShell } from './shell';

/** A key in the bar: drawn 36px, hit 44px tall through the pseudo-element. */
export const BAR_KEY = `relative inline-flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full text-sm font-medium np-state
  before:absolute before:-inset-y-1 before:inset-x-0 before:content-[''] ${FOCUS} ${MOTION}`;

/** The filled field inside a form: `m-0` because the global input rule gives inputs a margin. */
export const FIELD = `m-0 min-h-[2.75rem] w-full rounded-lg px-3 py-2 ${fieldBase} !text-base`;

export const SECTION = 'rounded-xl bg-surface-container-low p-4';

export const ScreenBar: React.FC<{
  title: React.ReactNode;
  onBack: () => void;
  backLabel?: string;
  right?: React.ReactNode;
}> = ({ title, onBack, backLabel = 'Back', right }) => (
  <div className="flex h-14 shrink-0 items-center gap-2 px-2">
    <button type="button" onClick={onBack} className={`${BAR_KEY} pl-1.5 pr-3 text-on-surface-variant hover:text-on-surface`}>
      <BackIcon className="h-5 w-5" />
      {backLabel}
    </button>
    <h1 className="min-w-0 truncate text-base font-semibold text-on-surface">{title}</h1>
    {right && <div className="ml-auto shrink-0">{right}</div>}
  </div>
);

interface ScanFieldProps {
  inputRef: React.RefObject<HTMLInputElement>;
  /** The visible words above the field, and its accessible name. */
  label: string;
  onSubmit: (code: string) => void;
  busy?: boolean;
}

/**
 * Focus a scan field for the next scan, but never inside the kiosk: the handhelds run
 * in HID mode, so scans reach the page's key listener (useScannerInput) with nothing
 * focused, and a focused field would raise the soft keyboard over the screen.
 */
export const focusForScan = (ref: React.RefObject<HTMLInputElement>) => {
  if (!inShell()) ref.current?.focus({ preventScroll: true });
};

/**
 * One plain text input. A scanner (or a person typing) enters the code and presses
 * Enter, which submits the form; the field clears on submit. In a browser it keeps
 * focus for the next scan; in the kiosk it lets go (focusForScan). NO inputMode="none":
 * it suppresses InputConnection on these handhelds (see hooks/useScannerInput).
 */
export const ScanField: React.FC<ScanFieldProps> = ({ inputRef, label, onSubmit, busy = false }) => {
  const [value, setValue] = useState('');
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const code = value.trim();
    if (!code) return;
    setValue('');
    onSubmit(code);
    // In the kiosk a hand-typed code drops the keyboard; the next scan arrives by HID.
    if (inShell()) inputRef.current?.blur();
    else inputRef.current?.focus({ preventScroll: true });
  };
  return (
    <form onSubmit={submit}>
      <label htmlFor="scanner-scan-field" className="mb-2 block text-center text-sm text-on-surface-variant">
        {label}
      </label>
      <div className={`flex items-center gap-1 rounded-full py-1.5 pl-4 pr-1.5 ring-1 ring-primary/30 ${fieldBase} focus-within:ring-2 focus-within:ring-primary/60`}>
        <ScanFrameIcon className="h-7 w-7 shrink-0 text-on-surface-variant" />
        <input
          id="scanner-scan-field"
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Scan or type a code"
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="go"
          className="m-0 min-h-[2.75rem] min-w-0 flex-1 bg-transparent px-2 font-mono text-lg text-on-surface placeholder:font-sans placeholder:text-base placeholder:text-on-surface-variant focus:outline-none"
        />
        <button
          type="submit"
          disabled={!value.trim() || busy}
          className={`inline-flex h-11 shrink-0 items-center whitespace-nowrap rounded-full bg-primary px-5 text-sm font-semibold text-on-primary np-state np-state-on-primary disabled:opacity-40 ${FOCUS} ${MOTION}`}
        >
          {busy ? 'Looking…' : 'Go'}
        </button>
      </div>
    </form>
  );
};

export interface KeyOption {
  key: string;
  label: string;
}

/**
 * A recessed track holding keys: the active one is a tint with accent text, the rest
 * recede. One choice at a time, so it is a radio group.
 */
export const KeyStrip: React.FC<{
  label: string;
  options: KeyOption[];
  value: string;
  onChange: (key: string) => void;
}> = ({ label, options, value, onChange }) => (
  <div
    role="radiogroup"
    aria-label={label}
    className="grid auto-cols-fr grid-flow-col gap-0.5 rounded-full bg-surface-container-highest p-0.5 shadow-[inset_0_1px_2px_rgba(0,0,0,0.35)]"
  >
    {options.map((o) => {
      const on = o.key === value;
      return (
        <button
          key={o.key}
          type="button"
          role="radio"
          aria-checked={on}
          onClick={() => onChange(o.key)}
          className={`min-h-[2.75rem] whitespace-nowrap rounded-full px-3 text-base font-semibold np-state ${FOCUS} ${MOTION}
            ${on ? 'bg-primary/15 text-primary' : 'text-on-surface-variant hover:text-on-surface'}`}
        >
          {o.label}
        </button>
      );
    })}
  </div>
);

/** A whole-number field with a step key each side. Enter submits the form around it. */
export const QuantityField: React.FC<{
  label: string;
  value: string;
  max: number;
  onChange: (next: string) => void;
  autoFocus?: boolean;
}> = ({ label, value, max, onChange, autoFocus = false }) => {
  const n = Number(value);
  const step = (by: number) => {
    const base = Number.isInteger(n) && n > 0 ? n : 0;
    onChange(String(Math.min(max, Math.max(1, base + by))));
  };
  const stepKey = `inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-surface-container-highest text-on-surface ${STATE_LAYER} disabled:opacity-40 ${FOCUS} ${MOTION}`;
  return (
    <div>
      <label htmlFor="scanner-quantity" className="mb-1 block text-sm text-on-surface-variant">{label}</label>
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => step(-1)} disabled={n <= 1} aria-label="One fewer" className={stepKey}>
          <MinusIcon className="h-5 w-5" />
        </button>
        <input
          id="scanner-quantity"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="off"
          value={value}
          autoFocus={autoFocus}
          onFocus={(e) => e.target.select()}
          onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))}
          className={`${FIELD} text-center font-mono tabular-nums`}
        />
        <button type="button" onClick={() => step(1)} disabled={n >= max} aria-label="One more" className={stepKey}>
          <PlusIcon className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

/** The answer to the last action, in place. Words and a glyph carry the tone, colour only reinforces it. */
export const Notice: React.FC<{ tone: 'ok' | 'miss'; children: React.ReactNode }> = ({ tone, children }) => (
  <p
    className={`flex items-start gap-2 rounded-xl px-3 py-2.5 text-base leading-snug motion-safe:animate-rowLand
      ${tone === 'ok' ? 'bg-green-900/40 text-green-100' : 'bg-red-900/40 text-red-100'}`}
  >
    {tone === 'ok'
      ? <CheckIcon className="mt-0.5 h-5 w-5 shrink-0" />
      : <WarningIcon className="mt-0.5 h-5 w-5 shrink-0" />}
    <span className="min-w-0 break-words">{children}</span>
  </p>
);
